import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
  selectAppliedBonus,
  selectCashierConfiguration,
} from '@cashier/cashier-data-access';
import {
  AmountInterval,
  FastSumsType,
  FastSumsTypes,
  IntervalsType,
  IntervalsTypes,
  PaymentTypes,
  RewardTypes,
} from '@cashier/cashier-model';

interface UseBonusConfiguration {
  getConfigurationByAmount: (amount: string) => BonusConfiguration | null;
}

export interface BonusConfiguration {
  interval: AmountInterval;
  displayCalculator: boolean;
  displayFastSums: boolean;
  isCalculatorActive: boolean;
  fastSumsType: FastSumsType;
  intervalsType: IntervalsType;
  effectiveFraction?: number | null;
}

export const useBonusConfiguration = (): UseBonusConfiguration => {
  const bonus = useSelector(selectAppliedBonus);
  const { paymentType } = useSelector(selectCashierConfiguration);

  const isAmountBelowMinimumDeposit = (
    amount: number,
    intervalsType: IntervalsType,
    intervals: AmountInterval[],
  ): boolean => {
    if (intervalsType !== IntervalsTypes.DepositRange) {
      return false;
    }

    const firstInterval = intervals[0];
    if (!firstInterval?.depositRange) {
      return false;
    }

    const minFrom = firstInterval.depositRange.from;
    return minFrom !== null && amount < minFrom;
  };

  const provideIntervalByDepositRange = (
    amount: number,
    intervals: AmountInterval[],
  ): { interval: AmountInterval; effectiveFraction?: number | null } | null => {
    const matchedIndex = intervals.findIndex((interval) => {
      if (!interval.depositRange) {
        return false;
      }

      const matchesFrom = amount >= (interval.depositRange.from ?? 0);
      const matchesTo =
        interval.depositRange.to === null || amount <= interval.depositRange.to;

      return matchesFrom && matchesTo;
    });

    if (matchedIndex === -1) {
      return null;
    }

    const matchedInterval = intervals[matchedIndex];

    // Special case: if matched interval is fixed and is the last interval,
    // use the previous interval's fraction for display
    if (
      matchedInterval.rewardType === RewardTypes.Fixed &&
      matchedIndex === intervals.length - 1 &&
      matchedIndex > 0
    ) {
      const previousInterval = intervals[matchedIndex - 1];
      return {
        interval: matchedInterval,
        effectiveFraction: previousInterval.attributes.fraction,
      };
    }

    return { interval: matchedInterval };
  };

  const provideIntervalByReward = (
    amount: number,
    intervals: AmountInterval[],
  ): AmountInterval | null => {
    return (
      intervals.find((interval) => {
        if (
          interval.rewardType === RewardTypes.Fixed ||
          interval.rewardType === RewardTypes.UnCountable
        ) {
          return true;
        }

        if (interval.rewardType === RewardTypes.Fractional) {
          const rewardAmount = amount * (interval.attributes?.fraction || 1);

          const from = interval.reward.from || 0;
          const to = interval.reward.to;

          // Interval starts from 0 and 0 amount is selected - show complete your payment message
          const isZeroAmountMatchInterval = amount === 0 && from === 0;

          return (
            !isZeroAmountMatchInterval &&
            from <= rewardAmount &&
            (to === null || to > rewardAmount)
          );
        }
      }) || null
    );
  };

  const getConfigurationByAmount = useCallback(
    (amount: string): BonusConfiguration | null => {
      if (!bonus) {
        return null;
      }

      const numericAmount = Number(amount?.replace(',', '.') || '0');

      let suitableInterval: AmountInterval | null = null;
      let effectiveFraction: number | null | undefined;

      const amountIntervals = bonus?.amountIntervals || [];
      const intervalsType = bonus?.intervalsType || IntervalsTypes.Reward;

      // Use intervalsType for bonus calculator matching
      if (intervalsType === IntervalsTypes.DepositRange) {
        // Hide bonus calculator if amount is empty for deposit range type
        if (!amount) {
          return null;
        }

        // Hide bonus calculator if amount is below minimum deposit range
        if (
          isAmountBelowMinimumDeposit(
            numericAmount,
            intervalsType,
            amountIntervals,
          )
        ) {
          return null;
        }

        const result = provideIntervalByDepositRange(
          numericAmount,
          amountIntervals,
        );
        if (result) {
          suitableInterval = result.interval;
          effectiveFraction = result.effectiveFraction;
        }
      } else {
        suitableInterval = provideIntervalByReward(
          numericAmount,
          amountIntervals,
        );
      }

      const isCalculatorActive =
        paymentType === PaymentTypes.Deposit &&
        !!suitableInterval &&
        suitableInterval.rewardType !== RewardTypes.UnCountable;

      if (!suitableInterval) {
        return null;
      }

      return {
        interval: suitableInterval,
        displayCalculator:
          bonus?.cashierConfiguration.displayBonusCalculator || false,
        displayFastSums:
          bonus?.cashierConfiguration.displayBonusFastSums || false,
        isCalculatorActive,
        fastSumsType: bonus?.fastSumsType || FastSumsTypes.Amount,
        intervalsType,
        effectiveFraction,
      };
    },
    [bonus, paymentType],
  );

  return { getConfigurationByAmount };
};
